export type PillarSlug =
  | "assist"
  | "pulse"
  | "track"
  | "predict"
  | "quote"
  | "plan"
  | "capacity"
  | "price"
  | "stock"
  | "maintain"
  | "ship"
  | "account"
  | "govern"
  | "regulate";

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  pillar: PillarSlug;
  /**
   * Optional: permissions sharing a group render as ONE card in the admin
   * picker, with each permission as a smaller option inside it. Ticking the
   * card grants every member; the options can also be ticked one by one.
   * Key into PERMISSION_GROUPS.
   */
  group?: PermissionGroupSlug;
}

export type PermissionGroupSlug = "accounts-payable";

export const PERMISSION_GROUPS: Record<PermissionGroupSlug, { label: string; description: string }> = {
  "accounts-payable": {
    label: "Accounts Payable",
    description: "All AP viewing: your own approval queue, the full inbox, and the item catalog. Tick the options below for less.",
  },
};

export const PILLAR_ORDER: PillarSlug[] = [
  "assist",
  "pulse",
  "track",
  "predict",
  "quote",
  "plan",
  "capacity",
  "price",
  "stock",
  "maintain",
  "ship",
  "account",
  "govern",
  "regulate",
];

export const PILLAR_LABELS: Record<PillarSlug, string> = {
  assist: "Assist",
  pulse: "Pulse",
  track: "Track",
  predict: "Predict",
  quote: "Quote",
  plan: "Plan",
  capacity: "Capacity",
  price: "Price",
  stock: "Stock",
  maintain: "Maintain",
  ship: "Ship",
  account: "Account",
  govern: "Govern",
  regulate: "Regulate",
};

export const PERM = {
  VIEW_ASSISTANT: "view_assistant",
  VIEW_DASHBOARD: "view_dashboard",
  VIEW_LABOR_STATS: "view_labor_stats",
  VIEW_HOURLY_REPORT: "view_hourly_report",
  VIEW_LABOR_RECONCILIATION: "view_labor_reconciliation",
  VIEW_PRODUCTION_RECONCILIATION: "view_production_reconciliation",
  VIEW_INSPECTION_RECONCILIATION: "view_inspection_reconciliation",
  VIEW_RATE_ANALYSIS: "view_rate_analysis",
  VIEW_RATE_CHANGES: "view_rate_changes",
  VIEW_LINE_CONFIGS: "view_line_configs",
  VIEW_WORK_ORDERS: "view_work_orders",
  VIEW_LOGISTICS: "view_logistics",
  VIEW_CUSTOMERS: "view_customers",
  VIEW_REPORTS: "view_reports",
  VIEW_SETTINGS: "view_settings",
  VIEW_MACHINES: "view_machines",
  VIEW_PALLETS: "view_pallets",
  VIEW_MATERIALS: "view_materials",
  VIEW_SHIPPERS: "view_shippers",
  VIEW_LOCATIONS: "view_locations",
  VIEW_PMS: "view_pms",
  VIEW_MACHINE_OEE: "view_machine_oee",
  VIEW_MRP: "view_mrp",
  VIEW_PARTS: "view_parts",
  VIEW_SCHEDULE: "view_schedule",
  EDIT_SCHEDULE: "edit_schedule",
  VIEW_FORECAST: "view_forecast",
  EDIT_FORECAST: "edit_forecast",
  VIEW_CAPACITY: "view_capacity",
  EDIT_CAPACITY: "edit_capacity",
  VIEW_ACI_LABOR_STATS: "view_aci_labor_stats",
  VIEW_UNETTE_LABOR_STATS: "view_unette_labor_stats",
  VIEW_CORPORATE_DLS: "view_corporate_dls",
  VIEW_PIPELINE: "view_pipeline",
  VIEW_AUDIT: "view_audit",
  VIEW_AUDIT_FINANCIAL: "view_audit_financial",
  VIEW_EMPLOYEE: "view_employee",
  VIEW_REPORT_CARD: "view_report_card",
  VIEW_ERRORS: "view_errors",
  MANAGE_ERRORS: "manage_errors",
  MANAGE_USERS: "manage_users",
  MANAGE_SECURITY: "manage_security",
  MANAGE_CONSTANTS: "manage_constants",
  MANAGE_SAVINGS: "manage_savings",
  MANAGE_AI: "manage_ai",
  VIEW_QUOTES: "view_quotes",
  EDIT_QUOTES: "edit_quotes",
  APPROVE_QUOTES: "approve_quotes",
  MANAGE_QUOTE_RATES: "manage_quote_rates",
  VIEW_BUDGET: "view_budget",
  EDIT_BUDGET_FORECAST: "edit_budget_forecast",
  VIEW_AP: "view_ap",
  VIEW_AP_CATALOG: "view_ap_catalog",
  VIEW_MY_APPROVALS: "view_my_approvals",
  MANAGE_AP: "manage_ap",
  APPROVE_AP: "approve_ap",
  DELETE_AP: "delete_ap",
  MANAGE_BC_ROUTING: "manage_bc_routing",
  VIEW_AR: "view_ar",
  MANAGE_AR_MAP: "manage_ar_map",
  MANAGE_AR: "manage_ar",
  VIEW_EBITDA: "view_ebitda",
  EDIT_EBITDA_CONFIG: "edit_ebitda_config",
  VIEW_BATCH_RECORDS: "view_batch_records",
  APPROVE_BATCH_RECORDS: "approve_batch_records",
  VIEW_CERTIFICATIONS: "view_certifications",
  VIEW_APR: "view_apr",
  VIEW_MOCK_RECALL: "view_mock_recall",
  VIEW_SUPPLIER_QUALITY: "view_supplier_quality",
  VIEW_AUDIT_MGMT: "view_audit_mgmt",
  VIEW_ETQ: "view_etq",
  VIEW_QMS_MAP: "view_qms_map",
  VIEW_DOCUMENT_CONTROL: "view_document_control",
  VIEW_STABILITY: "view_stability",
  MANAGE_QUALITY_RECORDS: "manage_quality_records",
} as const;

export type PermissionKey = (typeof PERM)[keyof typeof PERM];

export const ALL_PERMISSIONS: PermissionDef[] = [
  // Assist — the in-app AI assistant and AI toolset
  { key: PERM.VIEW_ASSISTANT, label: "AI Assistant", description: "Use the full-page AI assistant and AI toolset", pillar: "assist" },

  // Pulse — daily labor stats across facilities
  { key: PERM.VIEW_DASHBOARD, label: "Dashboard", description: "View the main dashboard", pillar: "pulse" },
  { key: PERM.VIEW_LABOR_STATS, label: "Praxis GR Labor Stats", description: "View Praxis GR labor statistics (DLS)", pillar: "pulse" },
  { key: PERM.VIEW_HOURLY_REPORT, label: "Hourly Report", description: "View, edit, and send the hourly production exception report", pillar: "pulse" },
  { key: PERM.VIEW_LABOR_RECONCILIATION, label: "Labor Reconciliation", description: "Reconcile Sugar scanned labor hours against ADP reviewed/paid hours", pillar: "account" },
  { key: PERM.VIEW_PRODUCTION_RECONCILIATION, label: "Production Reconciliation", description: "Flag order-parts where DLS-claimed production diverges from the Material Accountability (MAL) count", pillar: "account" },
  { key: PERM.VIEW_INSPECTION_RECONCILIATION, label: "Inspection Reconciliation", description: "Reconcile booked inspection hours against the CNQI that documents the customer material issue, and flag the unlinked ones", pillar: "account" },
  { key: PERM.VIEW_ACI_LABOR_STATS, label: "Praxis LL Labor Stats", description: "View Praxis LL (Nulogy) labor statistics and income", pillar: "pulse" },
  { key: PERM.VIEW_UNETTE_LABOR_STATS, label: "Praxis NJ Labor Stats", description: "View Praxis NJ (Nulogy) labor statistics", pillar: "pulse" },
  { key: PERM.VIEW_CORPORATE_DLS, label: "Corporate Labor Stats", description: "View combined corporate labor statistics across all facilities", pillar: "pulse" },

  // Track — order lifecycle from PO to ship
  { key: PERM.VIEW_PIPELINE, label: "Pipeline", description: "View order pipeline and income by status across facilities", pillar: "track" },
  { key: PERM.VIEW_WORK_ORDERS, label: "Work Orders", description: "View work orders", pillar: "track" },
  { key: PERM.VIEW_CUSTOMERS, label: "Customers", description: "View customer analytics and details", pillar: "track" },

  // Predict — forecasting
  { key: PERM.VIEW_FORECAST, label: "Forecasting", description: "View customer forecasts and published snapshots", pillar: "predict" },
  { key: PERM.EDIT_FORECAST, label: "Edit Forecast", description: "Create, edit, and publish forecast worksheets", pillar: "predict" },

  // Quote — quote assist and approvals
  { key: PERM.VIEW_QUOTES, label: "Quote Assist", description: "View Quote Assist and search similar past jobs", pillar: "quote" },
  { key: PERM.EDIT_QUOTES, label: "Edit Quotes", description: "Create and edit draft quotes (Phase 2)", pillar: "quote" },
  { key: PERM.APPROVE_QUOTES, label: "Approve Quotes", description: "Review, approve, or reject quotes submitted for approval", pillar: "quote" },
  { key: PERM.MANAGE_QUOTE_RATES, label: "Manage Quote Rates", description: "Edit labor, machine, and material rates used in quoting", pillar: "quote" },

  // Plan — scheduling
  { key: PERM.VIEW_SCHEDULE, label: "Scheduling", description: "View production scheduling and read-only schedule views", pillar: "plan" },
  { key: PERM.EDIT_SCHEDULE, label: "Edit Schedule", description: "Create and edit production schedules, publish plans", pillar: "plan" },

  // Capacity — what-if simulation
  { key: PERM.VIEW_CAPACITY, label: "Capacity Planning", description: "View capacity planning charts and utilization data", pillar: "capacity" },
  { key: PERM.EDIT_CAPACITY, label: "Edit Capacity", description: "Manage potential orders, machine type groups, and run simulations", pillar: "capacity" },

  // Price — rates and standards
  { key: PERM.VIEW_RATE_ANALYSIS, label: "Rate Analysis", description: "View rate analysis data", pillar: "price" },
  { key: PERM.VIEW_RATE_CHANGES, label: "Rate Changes", description: "View and manage rate changes", pillar: "price" },
  { key: PERM.VIEW_LINE_CONFIGS, label: "Line Configs", description: "View line configurations", pillar: "price" },

  // Stock — inventory and materials
  { key: PERM.VIEW_PALLETS, label: "Pallets", description: "View pallet tracking and inventory", pillar: "stock" },
  { key: PERM.VIEW_MATERIALS, label: "Materials", description: "View material inventory and usage", pillar: "stock" },
  { key: PERM.VIEW_PARTS, label: "Parts", description: "View parts catalog and BOMs", pillar: "stock" },
  { key: PERM.VIEW_LOCATIONS, label: "Locations", description: "View warehouse locations and zones", pillar: "stock" },
  { key: PERM.VIEW_MRP, label: "MRP", description: "View material requirements planning", pillar: "stock" },

  // Maintain — fleet health
  { key: PERM.VIEW_MACHINES, label: "Machines", description: "View machine equipment health dashboard", pillar: "maintain" },
  { key: PERM.VIEW_PMS, label: "PMs", description: "View preventive maintenance schedules", pillar: "maintain" },
  { key: PERM.VIEW_MACHINE_OEE, label: "OEE / Machine Signals", description: "View live PLC machine signals, OEE, run windows, and downtime (Ignition pilot)", pillar: "maintain" },

  // Ship — logistics
  { key: PERM.VIEW_LOGISTICS, label: "Logistics", description: "View logistics map and facilities", pillar: "ship" },
  { key: PERM.VIEW_SHIPPERS, label: "Shippers", description: "View shipper and carrier management", pillar: "ship" },

  // Account — finance
  { key: PERM.VIEW_BUDGET, label: "Budget", description: "View department budgets, actuals, and invoice-level expense detail", pillar: "account" },
  { key: PERM.EDIT_BUDGET_FORECAST, label: "Edit Budget Forecast", description: "Edit and publish the month-end forecast column on the budget worksheet", pillar: "account" },
  // AP viewing is three separate grants (shown as one "Accounts Payable" card
  // with three options) so a department approver can be given only their own
  // queue without seeing every vendor's bills.
  { key: PERM.VIEW_MY_APPROVALS, label: "AP My Approvals", description: "See and act on the vendor bills routed to you for approval (your own queue only)", pillar: "account", group: "accounts-payable" },
  { key: PERM.VIEW_AP, label: "AP Inbox", description: "View the AP inbox: every vendor bill, credit memo, parse status, and PDF", pillar: "account", group: "accounts-payable" },
  { key: PERM.VIEW_AP_CATALOG, label: "AP Item Catalog", description: "Browse the item catalog built from AP bill lines: part numbers, supplier part numbers, and price history", pillar: "account", group: "accounts-payable" },
  { key: PERM.MANAGE_AP, label: "Manage AP", description: "Upload, reparse, reassign vendors, attach POs, set GL, and edit parser templates", pillar: "account" },
  { key: PERM.APPROVE_AP, label: "Approve / Post AP", description: "Approve department bills and post them to Business Central", pillar: "account" },
  { key: PERM.DELETE_AP, label: "Delete AP Bills", description: "Delete bills from the AP inbox (a reversible soft delete: hidden everywhere, restorable, and the document can never re-ingest)", pillar: "account" },
  { key: PERM.MANAGE_BC_ROUTING, label: "BC Company Routing", description: "Choose which Business Central company each plant's AP invoices post into, and switch a plant between its test and live company", pillar: "account" },
  { key: PERM.VIEW_AR, label: "Accounts Receivable", description: "View AR: customer balances, aging, sales invoices, credits, payments, and the collections forecast", pillar: "account" },
  { key: PERM.MANAGE_AR_MAP, label: "Manage AR Customer Map", description: "Link Business Central customers to unified Operations customers (the AR mapping screen)", pillar: "account" },
  { key: PERM.MANAGE_AR, label: "Manage AR Collections", description: "Add and update collection notes, promise-to-pay dates, and follow-up status on the AR worklist", pillar: "account" },
  { key: PERM.VIEW_EBITDA, label: "EBITDA Estimate", description: "View the real-time month-to-date EBITDA estimate: bridge, posted-vs-estimate reconciliation, per-site breakdown, and month-end projection", pillar: "account" },
  { key: PERM.EDIT_EBITDA_CONFIG, label: "Edit EBITDA Config", description: "Edit the GL-to-EBITDA account map, material standard costs, and blended labor rates that drive the EBITDA estimate", pillar: "account" },

  // Govern — audit, reporting, settings, admin
  { key: PERM.VIEW_AUDIT, label: "Audit / Due Diligence", description: "View audit and due-diligence grading across facilities and companies", pillar: "govern" },
  { key: PERM.VIEW_AUDIT_FINANCIAL, label: "Audit Financials", description: "Also see Financial, Customer Concentration, and Supplier categories in the audit module", pillar: "govern" },
  { key: PERM.VIEW_EMPLOYEE, label: "Employees", description: "View and manage the employee directory (Sugar↔ADP mapping, team, shift, line-tech flag)", pillar: "govern" },
  { key: PERM.VIEW_REPORT_CARD, label: "Line Tech Report Card", description: "View and manage line-tech quarterly report cards", pillar: "govern" },
  { key: PERM.VIEW_REPORTS, label: "Reports", description: "View reports", pillar: "govern" },
  { key: PERM.VIEW_SETTINGS, label: "Settings", description: "View and edit settings", pillar: "govern" },
  { key: PERM.MANAGE_USERS, label: "User Management", description: "Manage users and permissions", pillar: "govern" },
  { key: PERM.MANAGE_SECURITY, label: "Security & Activity", description: "View user activity, geolocation, and security alerts; lock or force-logout accounts", pillar: "govern" },
  { key: PERM.MANAGE_CONSTANTS, label: "Assumed Constants", description: "View and override tunable business constants (labor rates, thresholds) that are otherwise hardcoded", pillar: "govern" },
  { key: PERM.MANAGE_SAVINGS, label: "Module Savings", description: "View and edit the estimated / realized savings attributed to each P.U.L.S.E. module", pillar: "govern" },
  { key: PERM.MANAGE_AI, label: "AI Administration", description: "Manage the PraxisAI platform: models, personas, MCP servers and tools, API keys, and AI integrations", pillar: "govern" },
  { key: PERM.VIEW_ERRORS, label: "Error Reports", description: "View platform error reports and trends", pillar: "govern" },
  { key: PERM.MANAGE_ERRORS, label: "Manage Error Reports", description: "Resolve, reopen, or archive reported error groups", pillar: "govern" },
  // Regulate: the cGMP quality record. Batch review, certifications, QMS.
  { key: PERM.VIEW_BATCH_RECORDS, label: "Batch Record Review", description: "View the executed batch record review queue, exceptions, and assembled customer release packets", pillar: "regulate" },
  { key: PERM.APPROVE_BATCH_RECORDS, label: "Release Batch Records", description: "Disposition a batch record (release or reject) and send the release packet to the customer", pillar: "regulate" },
  { key: PERM.VIEW_CERTIFICATIONS, label: "Certification & Expiry Register", description: "View registrations, licenses, quality agreements, and equipment certificates with their renewal windows", pillar: "regulate" },
  { key: PERM.VIEW_APR, label: "Annual Product Review", description: "View per-product annual review packets: batch and yield roll-ups, deviations, complaints, and trend charts", pillar: "regulate" },
  { key: PERM.VIEW_MOCK_RECALL, label: "Mock Recall", description: "Run forward and backward lot traces and produce the timed recall reconciliation packet", pillar: "regulate" },
  { key: PERM.VIEW_SUPPLIER_QUALITY, label: "Supplier Qualification", description: "View approved-supplier status, quality agreements, audit dates, and CoA receipt per lot", pillar: "regulate" },
  { key: PERM.VIEW_AUDIT_MGMT, label: "Audit Management", description: "View the customer and agency audit calendar, findings register, and readiness packets", pillar: "regulate" },
  { key: PERM.VIEW_ETQ, label: "ETQ Quality Dashboard", description: "View live ETQ QMS records: controlled documents, deviations, CAPAs, change controls, and complaints", pillar: "regulate" },
  { key: PERM.VIEW_QMS_MAP, label: "QMS Relationship Map", description: "View the controlled-document reference graph and run impact assessments on a proposed revision", pillar: "regulate" },
  { key: PERM.VIEW_DOCUMENT_CONTROL, label: "Document Control Sync", description: "View controlled documents that have drifted between ETQ and their downstream copies in Sugar Print Forms", pillar: "regulate" },
  { key: PERM.VIEW_STABILITY, label: "Stability Program", description: "View stability protocols, chamber inventory, pull schedules, and overdue pulls", pillar: "regulate" },
  { key: PERM.MANAGE_QUALITY_RECORDS, label: "Manage Quality Records", description: "Create and edit Regulate records: certificates, audit findings, supplier quality status, and stability protocols", pillar: "regulate" },
];

/**
 * Permissions that were split out of an older, broader one. When the new key
 * is first created in the database, everyone holding the old key is granted
 * the new one too, so a split never silently takes access away. Applied once
 * by the admin permissions auto-sync (see /api/admin/permissions).
 */
export const PERMISSION_BACKFILL_FROM: Readonly<Record<string, string>> = {
  // The item catalog used to ride on the AP inbox permission.
  [PERM.VIEW_AP_CATALOG]: PERM.VIEW_AP,
};

export const PERMISSION_KEYS = ALL_PERMISSIONS.map((p) => p.key);

const PERMISSION_KEY_SET = new Set<string>(PERMISSION_KEYS);

/**
 * Count only the grants that still map to a module in ALL_PERMISSIONS.
 *
 * `admin_user_permissions` rows outlive the code list: retiring a permission
 * leaves its rows behind, so a raw `permissions.length` can exceed the number
 * of modules that exist and render as "72 / 69" in the admin screens.
 */
export function countKnownPermissions(keys: string[]): number {
  return keys.reduce((n, key) => (PERMISSION_KEY_SET.has(key) ? n + 1 : n), 0);
}
